/* ==========================================================================
   CalmEden quantity-tier bundle selector + tiered free gifts.

   Jobs:
     1. PDP UI  - the Buy 1 / Buy 2 / Buy 3 Get 1 Free cards drive the product
                  form's hidden quantity input, the FREE GIFTS strip locks/
                  unlocks with the tier, and an optional add-on checkbox joins
                  the order.
     2. One-request add - the paid units, every newly earned $0 gift variant
                  and the selected add-on go to /cart/add.js in ONE `items`
                  request, owned here for forms containing [data-cb-root]
                  (cart-drawer.js owns every other product form and skips
                  these). The cart is pre-read so an existing cart that
                  crosses a gift threshold earns the difference in the same
                  request - never a second add for threshold gifts.
     3. Cart    - an idempotent reconciler repairs the gift set after later
                  quantity edits, removals or direct-cart injections, with a
                  zero-op postcondition every checkout gate waits on. Triggers
                  are one-per-cause: the add pipeline, quantity edits, a fresh
                  load and persisted bfcache restores.

   Gift safety is FAIL-CLOSED: only a variant Liquid verified as titled
   "Free Gift", priced exactly 0 and available may ever be rendered, unlocked
   or added. There is no paid-variant fallback - a missing or mis-priced $0
   variant drops the gift out of the ladder entirely.

   Gift variant ids, the positive eligibility registry and the ladders all
   come from Liquid:
     - snippets/calmeden-eligible-handles.liquid (live bundle-eligible
       collection only; an empty collection qualifies nothing)
     - snippets/calmeden-gift-config.liquid  ([data-cb-global], every page)
     - snippets/calmeden-bundle-selector.liquid ([data-cb-data], PDP override)
   Nothing store-specific is a JS constant any more.

   Deliberately NO MutationObserver and NO window.fetch wrapping.

   P4 HARD STOP (documented, not fixable in theme JS): a shopper can navigate
   directly to /checkout, bypassing every theme-side gate, and the public
   permanent $0 "Free Gift" variants stay purchasable until server-side
   checkout validation (or unpublishing those variants from the Online Store
   channel) lands. Theme JS removes/neutralizes injected $0 gift lines before
   IT enables checkout; only the platform can close the direct-navigation
   hole.
   ========================================================================== */
(function () {
  "use strict";

  if (window.__calmedenBundleLoaded) return;
  window.__calmedenBundleLoaded = true;

  var GIFT_PROPERTY = "_calmeden_gift";

  /* A line carrying `_calmeden_qualifying: 0` never counts toward the gift
     threshold. Add-on lines carry it, which keeps ANY current or future
     metafield-resolved add-on nonqualifying without disqualifying the
     product itself by handle. */
  var QUALIFYING_VETO_PROPERTY = "_calmeden_qualifying";

  /* ------------------------------------------------------------ config ---- */

  /* Built from the Liquid islands; see loadGlobalConfig(). Maps keyed by
     handle so a PDP override can patch ids without touching the ladders. */
  var GIFTS =
    {}; /* handle -> {handle,title,paidVariantId,price,cleanupVariantId,freeVariantId} */
  var GLOBAL_LADDER = []; /* ordered handles */
  var PRODUCT_LADDERS = {}; /* handle -> {steps:[..], gifts:[handles]} */
  var ELIGIBLE =
    {}; /* positive qualifying registry (bundle-eligible collection) */
  var NON_QUALIFYING = {};
  var FREE_VARIANT_IDS = {};
  var MAX_TIER = 3;
  var configReady = false;

  var moneyFormat = "${{amount}}";

  function readJSON(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent) || null;
    } catch (e) {
      return null;
    }
  }

  /* The config contract, exported for tests. loadGlobalConfig REJECTS
     incomplete shapes ({} included): a broken island must never silently
     disable qualification or let checkout open over an unknown cart. A gift
     may carry freeVariantId 0 (its $0 variant could not be verified); such a
     gift is omitted fail-closed - never substituted with a paid variant. */
  function validateGiftConfig(data) {
    function nonEmptyString(value) {
      return typeof value === "string" && value.trim().length > 0;
    }
    function positiveInteger(value) {
      return (
        typeof value === "number" &&
        isFinite(value) &&
        Math.floor(value) === value &&
        value > 0
      );
    }
    function nonNegativeInteger(value) {
      return (
        typeof value === "number" &&
        isFinite(value) &&
        Math.floor(value) === value &&
        value >= 0
      );
    }
    function uniqueStringArray(values, requireItems) {
      if (!Array.isArray(values)) return false;
      if (requireItems && !values.length) return false;
      var seen = {};
      for (var index = 0; index < values.length; index++) {
        var value = values[index];
        if (!nonEmptyString(value) || seen[value]) return false;
        seen[value] = true;
      }
      return true;
    }

    if (!data || typeof data !== "object" || Array.isArray(data)) return false;
    if (!nonEmptyString(data.moneyFormat)) return false;
    if (!positiveInteger(data.maxTier)) return false;
    /* An empty positive registry is valid and means no product qualifies.
       Missing/unpublished Admin truth must fail closed, not invalidate the
       entire cart module or revive a hard-coded eligibility snapshot. */
    if (!uniqueStringArray(data.eligible, false)) return false;
    if (!uniqueStringArray(data.nonQualifying, false)) return false;
    if (!Array.isArray(data.gifts) || !data.gifts.length) return false;
    if (!uniqueStringArray(data.globalLadder, true)) return false;
    if (data.globalLadder.length !== data.maxTier) return false;
    if (
      !data.productLadders ||
      typeof data.productLadders !== "object" ||
      Array.isArray(data.productLadders)
    )
      return false;
    var requiredNonQualifying = [
      "shipping-insurance",
      "sleep-drops",
      "sleep-guide",
      "silk-sleep-mask",
      "sleeping-earplugs",
      "cloud-pillowcase",
    ];
    for (var i = 0; i < requiredNonQualifying.length; i++) {
      if (data.nonQualifying.indexOf(requiredNonQualifying[i]) === -1)
        return false;
    }
    var requiredGifts = [
      "sleep-guide",
      "silk-sleep-mask",
      "sleeping-earplugs",
      "cloud-pillowcase",
    ];
    var requiredGlobalLadder = [
      "cloud-pillowcase",
      "silk-sleep-mask",
      "sleeping-earplugs",
    ];
    var byHandle = {};
    var paidVariantIds = {};
    var cleanupVariantIds = {};
    for (var giftIndex = 0; giftIndex < data.gifts.length; giftIndex++) {
      var gift = data.gifts[giftIndex];
      if (!gift || typeof gift !== "object" || Array.isArray(gift))
        return false;
      if (!nonEmptyString(gift.handle) || byHandle[gift.handle]) return false;
      if (!nonEmptyString(gift.title)) return false;
      if (!positiveInteger(gift.paidVariantId)) return false;
      if (!nonNegativeInteger(gift.price)) return false;
      if (!nonNegativeInteger(gift.cleanupVariantId)) return false;
      if (!nonNegativeInteger(gift.freeVariantId)) return false;
      if (paidVariantIds[gift.paidVariantId]) return false;
      if (
        gift.freeVariantId &&
        gift.freeVariantId !== gift.cleanupVariantId
      )
        return false;
      if (
        gift.cleanupVariantId &&
        cleanupVariantIds[gift.cleanupVariantId]
      )
        return false;
      paidVariantIds[gift.paidVariantId] = true;
      if (gift.cleanupVariantId)
        cleanupVariantIds[gift.cleanupVariantId] = true;
      byHandle[gift.handle] = gift;
    }
    for (var j = 0; j < requiredGifts.length; j++) {
      var g = byHandle[requiredGifts[j]];
      if (!g) return false;
    }
    for (var k = 0; k < data.globalLadder.length; k++) {
      var h = data.globalLadder[k];
      if (!byHandle[h] || h !== requiredGlobalLadder[k]) return false;
    }
    var productHandles = Object.keys(data.productLadders);
    for (var p = 0; p < productHandles.length; p++) {
      var productHandle = productHandles[p];
      var ladder = data.productLadders[productHandle];
      if (!nonEmptyString(productHandle)) return false;
      if (!ladder || typeof ladder !== "object" || Array.isArray(ladder))
        return false;
      if (!Array.isArray(ladder.steps) || !ladder.steps.length) return false;
      if (!uniqueStringArray(ladder.gifts, true)) return false;
      /* Sleep Guide is retained in the registry only to identify and remove
         legacy/directly injected $0 lines. It is never an earned gift. */
      if (ladder.gifts.indexOf("sleep-guide") !== -1) return false;
      if (
        ladder.steps.length !== ladder.gifts.length ||
        ladder.steps.length > data.maxTier
      )
        return false;
      var previousStep = 0;
      for (var stepIndex = 0; stepIndex < ladder.steps.length; stepIndex++) {
        var step = ladder.steps[stepIndex];
        if (
          !positiveInteger(step) ||
          step <= previousStep ||
          step > data.maxTier ||
          !byHandle[ladder.gifts[stepIndex]]
        )
          return false;
        previousStep = step;
      }
    }
    return true;
  }

  function loadGlobalConfig() {
    var data = readJSON(document.querySelector("[data-cb-global]"));
    if (!validateGiftConfig(data)) {
      /* configReady stays false: reconcile rejects and every gate stays
         closed; the boot lifecycle gate surfaces the error visibly. */
      configReady = false;
      return;
    }
    moneyFormat = data.moneyFormat;
    MAX_TIER = data.maxTier;
    data.eligible.forEach(function (h) {
      ELIGIBLE[h] = true;
    });
    data.nonQualifying.forEach(function (h) {
      NON_QUALIFYING[h] = true;
    });
    data.gifts.forEach(function (g) {
      GIFTS[g.handle] = g;
      if (g.cleanupVariantId)
        FREE_VARIANT_IDS[g.cleanupVariantId] = true;
    });
    GLOBAL_LADDER = data.globalLadder.filter(function (h) {
      return !!GIFTS[h];
    });
    var pl = data.productLadders;
    Object.keys(pl).forEach(function (handle) {
      PRODUCT_LADDERS[handle] = {
        steps: pl[handle].steps || [],
        gifts: (pl[handle].gifts || []).filter(function (h) {
          return !!GIFTS[h];
        }),
      };
    });
    configReady = true;
  }

  function registerFreeVariants(ids) {
    if (!Array.isArray(ids)) return;
    ids.forEach(function (id) {
      if (id) FREE_VARIANT_IDS[id] = true;
    });
  }

  /* ---------------------------------------------------------------- money -- */

  function formatMoney(cents) {
    var value = Number(cents) || 0;
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_m, name) {
      switch (name) {
        case "amount":
          return withSeparators((value / 100).toFixed(2), ",", ".");
        case "amount_no_decimals":
          return withSeparators(String(Math.round(value / 100)), ",", "");
        case "amount_with_comma_separator":
          return withSeparators((value / 100).toFixed(2), ".", ",");
        case "amount_no_decimals_with_comma_separator":
          return withSeparators(String(Math.round(value / 100)), ".", "");
        case "amount_with_apostrophe_separator":
          return withSeparators((value / 100).toFixed(2), "'", ".");
        default:
          return (value / 100).toFixed(2);
      }
    });
  }

  function withSeparators(str, thousands, decimal) {
    var parts = String(str).split(".");
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    if (parts.length < 2 || !decimal) return whole;
    return whole + decimal + parts[1];
  }

  /* -------------------------------------------------------- tier maths ---- */

  /* Integer-cent maths that mirrors what Shopify's automatic percentage
     discount ACTUALLY does - measured on live carts 2026-08-07, not assumed:
     it floors the discount PER UNIT and then multiplies by the quantity.
     offCents is an additional fixed amount off the tier total (e.g. the
     Cervical pilot's Admin $19.99 off at quantity two). Kept identical to the
     Liquid render in snippets/calmeden-bundle-selector.liquid. */
  function tierPay(unitPrice, payUnits, payPct, offCents) {
    var offPct = 100 - (Number(payPct) || 0);
    var unitPay = Number(unitPrice) || 0;
    if (offPct > 0) {
      unitPay = unitPay - Math.floor((unitPay * offPct) / 100);
    }
    return unitPay * (Number(payUnits) || 0) - (Number(offCents) || 0);
  }

  /* ------------------------------------------------------- gift safety ---- */

  /* Fail-closed: only the Liquid-verified $0 "Free Gift" variant may ever be
     added. A paid variant is never a substitute for a missing free one. */
  function giftFreeVariantId(gift) {
    if (!gift || !gift.freeVariantId) return 0;
    return Number(gift.freeVariantId) || 0;
  }

  /* One-request add payload for the tier alone: paid units at the selected
     tier quantity plus every VERIFIED gift the tier earns (as $0 variants
     tagged with the gift property) and the add-on when its checkbox is on.
     A gift with free:false (no verified zero-price variant) is skipped -
     never a paid fallback. Pure - exported for tests. */
  function buildAddItems(variantId, tier, gifts, addon) {
    var items = [{ id: Number(variantId), quantity: Number(tier.qty) || 1 }];
    (gifts || []).forEach(function (g) {
      if (!g || !g.variantId || Number(g.index) > Number(tier.step)) return;
      if (g.free !== true) return;
      var props = {};
      props[GIFT_PROPERTY] = "1";
      items.push({ id: Number(g.variantId), quantity: 1, properties: props });
    });
    if (addon && addon.variantId) {
      var addonProps = {};
      addonProps[QUALIFYING_VETO_PROPERTY] = "0";
      items.push({
        id: Number(addon.variantId),
        quantity: 1,
        properties: addonProps,
      });
    }
    return items;
  }

  /* The full one-request payload, given the CURRENT cart. The existing cart
     counts toward the gift threshold, so a cart that crosses a tier with this
     add earns the difference in the SAME request - no second add for
     threshold gifts. Gifts already sitting in the cart as owned lines are not
     re-added; the reconciler remains the idempotent repair for every later
     drift. Pure - exported for tests. */
  function planAddItems(params) {
    var tier = params.tier || { step: 1, qty: 1 };
    var items = [
      { id: Number(params.variantId), quantity: Number(tier.qty) || 1 },
    ];

    /* Verified $0 gifts this PDP advertises, keyed by handle. A handle this
       PDP explicitly marks free:false is BLOCKED - its render could not
       verify the $0 variant, so no global fallback may resurrect it. */
    var pdpGifts = {};
    var pdpBlocked = {};
    (params.gifts || []).forEach(function (g) {
      if (!g || !g.handle) return;
      if (g.free !== true || !g.variantId) {
        pdpBlocked[g.handle] = true;
        return;
      }
      pdpGifts[g.handle] = g;
    });

    /* Accumulated eligibility: existing cart lines + this add. The add-on and
       every excluded handle are filtered inside wantedGifts. */
    var config = params.config || null;
    var giftsMap = (config && config.gifts) || GIFTS;
    var cartItems = Array.isArray(params.cartItems) ? params.cartItems : [];
    var lines = cartItems.concat([
      { handle: params.handle || "", quantity: Number(tier.qty) || 1 },
    ]);
    var wanted = wantedGifts(lines, config);

    var present = {};
    cartItems.forEach(function (item) {
      var handle = item.handle || "";
      var ids = (config && config.freeVariantIds) || FREE_VARIANT_IDS;
      if (giftsMap[handle] && isOurGiftLine(item, ids)) present[handle] = true;
    });

    Object.keys(wanted).forEach(function (handle) {
      if (present[handle] || pdpBlocked[handle]) return;
      var pdp = pdpGifts[handle];
      var variantId = pdp
        ? Number(pdp.variantId)
        : giftFreeVariantId(giftsMap[handle]);
      if (!variantId) return;
      var props = {};
      props[GIFT_PROPERTY] = "1";
      items.push({ id: variantId, quantity: 1, properties: props });
    });

    if (params.addon && params.addon.variantId) {
      var addonProps = {};
      addonProps[QUALIFYING_VETO_PROPERTY] = "0";
      items.push({
        id: Number(params.addon.variantId),
        quantity: 1,
        properties: addonProps,
      });
    }
    return items;
  }

  /* ------------------------------------------------------------------ UI --- */

  function initRoot(root) {
    if (root.__cbInit) return;
    root.__cbInit = true;

    var data = readJSON(root.querySelector("[data-cb-data]")) || {};
    if (data.moneyFormat) moneyFormat = data.moneyFormat;

    /* Liquid is the source of truth for gift variant ids - patch the global
       gift map so a re-created variant is picked up without a code edit. Only
       gifts Liquid verified as $0 "Free Gift" variants are registered; a paid
       variant is never promoted into a gift slot. */
    if (Array.isArray(data.gifts)) {
      data.gifts.forEach(function (g) {
        if (!g || !g.handle) return;
        if (!GIFTS[g.handle]) GIFTS[g.handle] = { handle: g.handle };
        GIFTS[g.handle].title = g.title || GIFTS[g.handle].title;
        if (g.free === true && g.variantId) {
          GIFTS[g.handle].freeVariantId = g.variantId;
          registerFreeVariants([g.variantId]);
        }
      });
    }

    var variants = Array.isArray(data.variants) ? data.variants : [];
    var addon = data.addon || null;
    var form = root.closest("form");
    var qtyInput = form ? form.querySelector('[name="quantity"]') : null;
    var variantInput = form ? form.querySelector('[name="id"]') : null;

    var tierEls = Array.prototype.slice.call(
      root.querySelectorAll("[data-cb-tier]"),
    );
    var giftEls = Array.prototype.slice.call(
      root.querySelectorAll("[data-cb-gift]"),
    );
    var addonInput = root.querySelector("[data-cb-addon-input]");

    var tiers =
      Array.isArray(data.tiers) && data.tiers.length
        ? data.tiers
        : tierEls.map(function (btn, i) {
            var q = parseInt(btn.getAttribute("data-cb-tier"), 10) || i + 1;
            return {
              step: i + 1,
              qty: q,
              payUnits: q,
              payPct: 100,
              offCents: 0,
              units: q,
            };
          });

    function tierByStep(step) {
      for (var i = 0; i < tiers.length; i++) {
        if (Number(tiers[i].step) === Number(step)) return tiers[i];
      }
      return tiers[0];
    }

    function currentVariant() {
      if (!variantInput) return variants[0] || null;
      var id = String(variantInput.value || "");
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === id) return variants[i];
      }
      return variants[0] || null;
    }

    function paintPrices() {
      var v = currentVariant();
      if (!v) return;
      var anchor =
        v.compareAtPrice && v.compareAtPrice > v.price
          ? v.compareAtPrice
          : v.price;
      tierEls.forEach(function (btn) {
        var t = tierByStep(btn.getAttribute("data-cb-step"));
        if (!t) return;
        var pay = tierPay(v.price, t.payUnits, t.payPct, t.offCents);
        var compare = anchor * t.units;
        var total = btn.querySelector("[data-cb-tier-total]");
        var cmp = btn.querySelector("[data-cb-tier-compare]");
        if (total) total.textContent = formatMoney(pay);
        if (cmp) {
          if (compare > pay) {
            cmp.textContent = formatMoney(compare);
            cmp.hidden = false;
          } else {
            cmp.textContent = "";
            cmp.hidden = true;
          }
        }
      });
    }

    function select(step, focusSelected) {
      var s = Math.min(tiers.length, Math.max(1, parseInt(step, 10) || 1));
      var tier = tierByStep(s);
      root.setAttribute("data-cb-selected", String(s));

      tierEls.forEach(function (btn) {
        var on = Number(btn.getAttribute("data-cb-step")) === s;
        btn.classList.toggle("is-selected", on);
        btn.setAttribute("aria-checked", on ? "true" : "false");
        btn.setAttribute("tabindex", on ? "0" : "-1");
        if (on && focusSelected && typeof btn.focus === "function") {
          btn.focus();
        }
      });

      giftEls.forEach(function (li) {
        var idx = parseInt(li.getAttribute("data-cb-gift"), 10) || 0;
        var unlocked = idx <= s;
        li.classList.toggle("is-unlocked", unlocked);
        li.classList.toggle("is-locked", !unlocked);
        var hint = li.querySelector("[data-cb-gift-hint]");
        if (hint) {
          if (unlocked) {
            hint.textContent = "Unlocked";
          } else {
            /* Phrase the hint in units the shopper has to add, which is the
               difference in PAID units, not in cart quantity. */
            var need = tierByStep(idx);
            var missing =
              (need ? need.payUnits : idx) - (tier ? tier.payUnits : s);
            if (missing < 1) missing = 1;
            hint.textContent = "Add " + missing + " more to unlock";
          }
        }
      });

      if (qtyInput && tier && String(qtyInput.value) !== String(tier.qty)) {
        qtyInput.value = String(tier.qty);
        qtyInput.dispatchEvent(new Event("input", { bubbles: true }));
        qtyInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    tierEls.forEach(function (btn) {
      btn.addEventListener("click", function (event) {
        event.preventDefault();
        select(btn.getAttribute("data-cb-step"), false);
      });
      btn.addEventListener("keydown", function (event) {
        var key = event.key;
        var current = tierEls.indexOf(btn);
        var next = current;
        if (key === "ArrowRight" || key === "ArrowDown") {
          next = (current + 1) % tierEls.length;
        } else if (key === "ArrowLeft" || key === "ArrowUp") {
          next = (current - 1 + tierEls.length) % tierEls.length;
        } else if (key === "Home") {
          next = 0;
        } else if (key === "End") {
          next = tierEls.length - 1;
        } else {
          return;
        }
        event.preventDefault();
        select(tierEls[next].getAttribute("data-cb-step"), true);
      });
    });

    if (variantInput) {
      variantInput.addEventListener("change", paintPrices);
    }

    root.__cbState = {
      tiers: tiers,
      tierByStep: tierByStep,
      gifts: Array.isArray(data.gifts) ? data.gifts : [],
      addon: addon,
      addonInput: addonInput,
      variantInput: variantInput,
      selected: function () {
        return tierByStep(root.getAttribute("data-cb-selected") || 1);
      },
    };

    paintPrices();
    select(root.getAttribute("data-cb-selected") || 1, false);
  }

  function initAll() {
    Array.prototype.slice
      .call(document.querySelectorAll("[data-cb-root]"))
      .forEach(initRoot);
  }

  /* -------------------------------------------------- one-request add ----- */

  /* Sole owner of AJAX submission for product forms containing [data-cb-root].
     cart-drawer.js skips these forms by the same test, so there is exactly one
     interceptor per form. The paid units, earned gifts and the add-on travel
     in a single /cart/add.js `items` request; the drawer renders from that
     response directly - no debounce, no sequential gift mutations in front of
     the shopper. */
  var addBusy = false;

  function onSubmit(event) {
    if (event.defaultPrevented) return;
    var form = event.target;
    if (!form || form.tagName !== "FORM") return;
    var action = form.getAttribute("action") || "";
    if (action.indexOf("/cart/add") === -1) return;
    var root = form.querySelector("[data-cb-root]");
    if (!root) return;

    var cart = window.CalmEdenCart;
    if (!configReady) {
      event.preventDefault();
      if (cart && typeof cart.setCheckoutGated === "function") {
        cart.setCheckoutGated(true);
      }
      showBundleError(
        form,
        "Your cart could not be updated. Please reload the page and try again.",
      );
      return;
    }
    if (!cart || typeof cart.addItems !== "function") {
      /* A bundle root exists but the full-offer capability (the shared cart
         pipeline) is missing: a native submit would silently drop the
         verified gifts and the selected add-on, which exist only in JS.
         Block instead, with an accessible error. */
      if (root) {
        event.preventDefault();
        showBundleError(
          form,
          "Your cart could not be updated. Please reload the page and try again.",
        );
      }
      return;
    }

    if (!root.__cbInit) initRoot(root);
    var state = root.__cbState || {};
    var tier = state.selected ? state.selected() : { step: 1, qty: 1 };
    var variantId = state.variantInput ? state.variantInput.value : "";
    if (!variantId) {
      /* A bundle form without a resolved paid variant is malformed. Native
         submission would silently omit every JS-only gift/add-on, so block it
         and surface the same accessible recovery path as a missing cart API. */
      event.preventDefault();
      showBundleError(
        form,
        "Please choose an available option before adding this bundle.",
      );
      return;
    }

    /* No drawer on this page: NEVER silently native-submit an advertised
       bundle - its verified $0 gifts and selected add-on exist only in JS.
       The truthful full-offer fallback below preserves the selected paid
       quantity, every earned verified gift and the selected add-on in one
       validated JSON add, reconciles and proves the zero-op postcondition,
       gates checkout via the shared barrier for the whole job, and only then
       navigates to the full cart. On any failure it stays on the page with
       an accessible error - no partial native submit, no omitted lines. */
    if (!document.querySelector("cart-drawer")) {
      event.preventDefault();
      if (addBusy) return;
      addBusy = true;
      /* Gate BEFORE enqueueing: the whole fallback is one barrier job, and a
         failure must leave the desired state gated, never re-enabled. */
      if (cart && typeof cart.setCheckoutGated === "function") {
        cart.setCheckoutGated(true);
      }
      var fallbackAddon =
        state.addon && state.addonInput && state.addonInput.checked
          ? state.addon
          : null;
      var fallbackWork = function () {
        return getCart()
          .catch(function () {
            return null;
          })
          .then(function (existing) {
            var items = planAddItems({
              cartItems:
                existing && Array.isArray(existing.items) ? existing.items : [],
              handle: root.getAttribute("data-cb-handle") || "",
              variantId: variantId,
              tier: tier,
              gifts: state.gifts || [],
              addon: fallbackAddon,
            });
            return postValidatedAdd(items)
              .then(function () {
                return reconcile();
              })
              .then(function () {
                /* final validated proof before navigating */
                return getCart();
              })
              .then(function () {
                window.location.assign
                  ? window.location.assign("/cart")
                  : (window.location.href = "/cart");
              });
          });
      };
      var fallbackRun =
        cart && typeof cart.transact === "function"
          ? cart.transact(fallbackWork)
          : fallbackWork();
      fallbackRun.then(
        function () {
          addBusy = false;
        },
        function (err) {
          addBusy = false;
          /* stays desired-gated on failure */
          if (cart && typeof cart.setCheckoutGated === "function") {
            cart.setCheckoutGated(true);
          }
          showBundleError(
            form,
            (err && err.message) ||
              "Your cart could not be updated. Please try again.",
          );
        },
      );
      return;
    }

    event.preventDefault();
    /* In-flight mutex: a second submit while one add is running is swallowed,
       never double-posted. */
    if (addBusy || (typeof cart.isAdding === "function" && cart.isAdding()))
      return;
    addBusy = true;

    var addon =
      state.addon && state.addonInput && state.addonInput.checked
        ? state.addon
        : null;

    /* Same task as the tap: guaranteed optimistic feedback (main line,
       tier-earned VERIFIED gifts, add-on) is handed to the shared pipeline,
       which starts the mutex, gates checkout, marks calmeden:tap, paints and
       opens the drawer NOW. The existing-cart pre-read then runs inside the
       pipeline as `prepare` - the tap-to-* measures span it - and the paid
       item + all missing verified gifts + add-on still leave in exactly one
       /cart/add.js items request. */
    var optimistic = [cart.readOptimisticLine(form, new FormData(form))];
    (state.gifts || []).forEach(function (g) {
      if (
        !g ||
        g.free !== true ||
        !g.variantId ||
        Number(g.index) > Number(tier.step)
      )
        return;
      optimistic.push({
        title: g.title || "Free gift",
        image: "",
        quantity: 1,
        note: "Free gift",
      });
    });
    if (addon) {
      optimistic.push({
        title: addon.title || "Add-on",
        image: "",
        quantity: 1,
        note: "Add-on",
      });
    }

    cart
      .addItems(null, optimistic, form, {
        prepare: function () {
          /* Pre-read the cart so an existing cart that crosses a gift threshold
           earns the difference in the SAME single add request. If the pre-read
           fails we degrade to tier-local gifts - still one request. */
          return getCart()
            .catch(function () {
              return null;
            })
            .then(function (existing) {
              return {
                items: planAddItems({
                  cartItems:
                    existing && Array.isArray(existing.items)
                      ? existing.items
                      : [],
                  handle: root.getAttribute("data-cb-handle") || "",
                  variantId: variantId,
                  tier: tier,
                  gifts: state.gifts || [],
                  addon: addon,
                }),
                sections: ["cart-drawer"],
                sections_url: window.location.pathname,
              };
            });
        },
        /* Checkout stays gated until the reconciler has settled AND proven the
         cart clean against this fresh state (direct-injection cleanup,
         dedupe, legacy repairs). A settle rejection keeps checkout gated.
         This hook runs INSIDE the add transaction, so composition uses the
         private refreshDrawerNow - the public queued refresh would deadlock
         the barrier. */
        settle: function () {
          return reconcile().then(function (changed) {
            if (changed && typeof cart.refreshDrawerNow === "function") {
              return cart.refreshDrawerNow();
            }
          });
        },
      })
      .then(
        function () {
          addBusy = false;
        },
        function () {
          addBusy = false;
        },
      );
  }

  /* -------------------------------------------------------------- reconcile */

  var busy = false;
  var pending = false;
  var settlePromise = null;
  var lastSettledCart = null;

  /* Fail closed: a non-2xx cart response or a body that is not a cart throws.
     Reconciliation and checkout gating depend on a truthful read, so errors
     are never swallowed here. */
  function jsonFetch(url, options) {
    var opts = options || {};
    opts.credentials = "same-origin";
    opts.headers = Object.assign(
      { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
      opts.headers || {},
    );
    return Promise.resolve()
      .then(function () {
        return window.fetch(url, opts);
      })
      .then(function (res) {
        return res.json().then(function (body) {
          if (!res.ok)
            throw new Error("cart request failed: HTTP " + res.status);
          return body;
        });
      });
  }

  function getCart() {
    return jsonFetch("/cart.js?cb=" + Date.now(), { method: "GET" }).then(
      function (body) {
        if (
          !body ||
          typeof body.item_count !== "number" ||
          !Array.isArray(body.items)
        )
          throw new Error("invalid cart payload");
        return body;
      },
    );
  }

  /* The no-drawer full-offer fallback's add: jsonFetch already rejects
     non-2xx; here the echoed items must also represent every requested
     variant id with at least the requested quantity - a partial add can
     never sneak through as success. */
  function postValidatedAdd(items) {
    return jsonFetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: items }),
    }).then(function (parsed) {
      if (parsed && parsed.status) {
        throw new Error(parsed.description || parsed.message || "add failed");
      }
      var got = parsed && parsed.items;
      if (!Array.isArray(got)) throw new Error("invalid add payload");
      items.forEach(function (req) {
        var found = null;
        got.forEach(function (item) {
          if (Number(item.variant_id || item.id) === Number(req.id))
            found = item;
        });
        if (!found || Number(found.quantity) < Number(req.quantity)) {
          throw new Error("add response mismatch");
        }
      });
      return parsed;
    });
  }

  /* Accessible on-page error for the no-drawer fallback: a role=alert node
     appended to the form (plus the shared cart error surface when present). */
  function showBundleError(form, message) {
    var cart = window.CalmEdenCart;
    if (cart && typeof cart.showCartError === "function") {
      cart.showCartError(message);
    }
    var el = null;
    if (form && typeof form.querySelector === "function") {
      el = form.querySelector("[data-cb-error]");
      if (!el && typeof document.createElement === "function") {
        el = document.createElement("div");
        el.className = "cb-form-error";
        el.setAttribute("role", "alert");
        el.setAttribute("data-cb-error", "");
        form.appendChild(el);
      }
    }
    if (el) el.textContent = message;
  }

  /* A line the reconciler owns. BOTH halves of the interlock are mandatory,
     whichever route the line arrived by:
       - its variant id must be a Liquid-verified $0 "Free Gift" variant id,
       - and the line price must be exactly 0.
     The `_calmeden_gift` property alone can never make a line ours: a paid
     line injected with the property stays a paid shopper line - reconcile
     will not remove it, reclassify it, or count it as the earned gift. The
     property survives only as the dedupe preference marker (isTaggedGiftLine)
     between two otherwise-identical verified $0 lines. */
  function isOurGiftLine(item, freeVariantIds) {
    var ids = freeVariantIds || FREE_VARIANT_IDS;
    var vid = item.variant_id || item.id;
    return !!ids[vid] && Number(item.price) === 0;
  }

  function isTaggedGiftLine(item) {
    var props = item.properties || {};
    return String(props[GIFT_PROPERTY] || "") === "1";
  }

  /* Which gifts should be in the cart right now, given what is in it.
     Qualification is POSITIVE: a line counts only when its handle is in the
     Liquid-rendered bundle-eligible registry (or drives a configured product
     ladder); the nonQualifying list is a second, independent veto for the
     excluded products and the add-on. Fail-closed: a gift whose $0 variant
     Liquid could not verify is never wanted. Pure - exported for tests.
     Returns {handle: giftObject}. */
  function wantedGifts(items, config) {
    var conf = config || {};
    var gifts = conf.gifts || GIFTS;
    var globalLadder = conf.globalLadder || GLOBAL_LADDER;
    var productLadders = conf.productLadders || PRODUCT_LADDERS;
    var eligible = conf.eligible || ELIGIBLE;
    var nonQualifying = conf.nonQualifying || NON_QUALIFYING;
    var maxTier = conf.maxTier || MAX_TIER;

    function verifiedGift(handle) {
      var g = gifts[handle];
      if (!g || !giftFreeVariantId(g)) return null;
      return g;
    }

    var qualifying = 0;
    var ladderQty = {};

    (items || []).forEach(function (item) {
      var handle = item.handle || "";
      var props = item.properties || {};
      if (String(props[QUALIFYING_VETO_PROPERTY] || "") === "0") return;
      if (gifts[handle]) return;
      if (nonQualifying[handle]) return;
      if (productLadders[handle]) {
        ladderQty[handle] = (ladderQty[handle] || 0) + (item.quantity || 0);
        return;
      }
      if (!eligible[handle])
        return; /* positive registry: not listed, never counts */
      qualifying += item.quantity || 0;
    });

    var wanted = {};

    var tier = Math.min(maxTier, qualifying);
    /* Tier positions are fixed. An unavailable gift is omitted from its own
       earned prefix; later gifts never move forward into an earlier tier. */
    globalLadder.slice(0, tier).forEach(function (h) {
      var g = verifiedGift(h);
      if (g) wanted[h] = g;
    });

    Object.keys(ladderQty).forEach(function (handle) {
      var conf2 = productLadders[handle];
      var qty = ladderQty[handle];
      var unlocked = 0;
      for (var i = 0; i < conf2.steps.length; i++) {
        if (qty >= conf2.steps[i]) unlocked = i + 1;
      }
      conf2.gifts.slice(0, unlocked).forEach(function (h) {
        var g = verifiedGift(h);
        if (g && !wanted[h]) wanted[h] = g;
      });
    });

    return wanted;
  }

  /* The repair operations for a cart snapshot: remove or trim owned gift
     lines that no longer belong (including neutralizing a directly injected
     unearned $0 gift), add the missing verified $0 gifts. When a shopper
     self-added the same gift we also own, OUR tagged line is the survivor.
     Pure - exported for tests. */
  function planReconcileOps(items, config) {
    var conf = config || {};
    var gifts = conf.gifts || GIFTS;
    var freeVariantIds = conf.freeVariantIds || FREE_VARIANT_IDS;

    var ourGifts = {};
    (items || []).forEach(function (item) {
      var handle = item.handle || "";
      if (gifts[handle] && isOurGiftLine(item, freeVariantIds)) {
        if (!ourGifts[handle]) ourGifts[handle] = [];
        ourGifts[handle].push(item);
      }
    });

    Object.keys(ourGifts).forEach(function (handle) {
      ourGifts[handle].sort(function (a, b) {
        return (isTaggedGiftLine(b) ? 1 : 0) - (isTaggedGiftLine(a) ? 1 : 0);
      });
    });

    var wanted = wantedGifts(items, config);
    var ops = [];

    Object.keys(ourGifts).forEach(function (handle) {
      ourGifts[handle].forEach(function (item, i) {
        var keep = !!wanted[handle] && i === 0;
        var target = keep ? 1 : 0;
        if (item.quantity !== target) {
          ops.push({ type: "change", key: item.key, quantity: target });
        }
      });
    });

    Object.keys(wanted).forEach(function (handle) {
      if (ourGifts[handle] && ourGifts[handle].length) return;
      var id = giftFreeVariantId(wanted[handle]);
      if (id) ops.push({ type: "add", variantId: id });
    });

    return ops;
  }

  /* Returns a promise that resolves only after the gift set has fully
     settled AND been proven settled, so checkout gating can await a truthful
     cart. Fail closed throughout: read or operation failures reject, and
     after the operations the final cart is re-read - planReconcileOps must
     find nothing left to repair, otherwise the promise rejects and checkout
     stays gated. Concurrent triggers collapse into one follow-up pass
     (busy/pending), and every waiter shares the same settle promise (and its
     rejection). Resolves to true when the cart was actually modified. */
  function reconcile() {
    /* Missing global gift config is a broken render: reject, so every caller
       keeps checkout gated instead of treating an unknown cart as clean. */
    if (!configReady) return Promise.reject(new Error("gift config missing"));
    if (busy) {
      pending = true;
      return settlePromise || Promise.resolve(false);
    }
    busy = true;

    var changed = false;

    settlePromise = getCart()
      .then(function (cart) {
        var ops = planReconcileOps(cart.items);

        if (!ops.length) return null;

        /* Sequential: the AJAX cart API is not safe to hammer in parallel. */
        return ops.reduce(function (chain, op) {
          return chain.then(function () {
            if (op.type === "change") {
              return jsonFetch("/cart/change.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id: op.key, quantity: op.quantity }),
              }).then(function () {
                changed = true;
              });
            }
            var props = {};
            props[GIFT_PROPERTY] = "1";
            return jsonFetch("/cart/add.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                items: [{ id: op.variantId, quantity: 1, properties: props }],
              }),
            }).then(function () {
              changed = true;
            });
          });
        }, Promise.resolve());
      })
      .then(function () {
        /* Postcondition: the planner must find the final cart clean. */
        return getCart().then(function (finalCart) {
          if (planReconcileOps(finalCart.items).length) {
            throw new Error("gift reconciliation did not settle");
          }
          lastSettledCart = finalCart;
        });
      })
      .then(
        function () {
          busy = false;
          if (pending) {
            pending = false;
            return reconcile().then(function (more) {
              return changed || more;
            });
          }
          settlePromise = null;
          return changed;
        },
        function (err) {
          busy = false;
          settlePromise = null;
          lastSettledCart = null;
          throw err;
        },
      );
    return settlePromise;
  }

  /* Lifecycle repair gate (fresh load, persisted bfcache restores). Checkout
     is gated for the duration of the work; on success the previous button
     state is restored (or the refreshed server truth takes over), on failure
     checkout stays gated and the error is shown - fail closed. */
  /* Lifecycle repair gate (fresh load, persisted bfcache restores).
     desired-gates BEFORE enqueueing, then runs through cart.transact (which
     ALWAYS enqueues - no nested public queue calls). Inside the owning
     transaction, composition uses the private refreshDrawerNow only. The
     final checkout state always comes from an AUTHORITATIVE validated read:
     a changed cart is proven by the refresh; an unchanged cart is proven by
     a final getCart - never by a captured physical button state. Any failure
     remains gated and visible. */
  function reconcileAndRefresh() {
    var cart = window.CalmEdenCart;
    if (cart && typeof cart.setCheckoutGated === "function") {
      cart.setCheckoutGated(true);
    }
    var work = function () {
      return reconcile().then(function (changed) {
        if (changed && cart && typeof cart.refreshDrawerNow === "function") {
          /* the refresh proves and records the final state */
          return cart.refreshDrawerNow();
        }
        /* unchanged: reuse reconcile's validated postcondition cart instead
           of issuing a third boot read. Apply all empty/count/gate state from
           that one authoritative object. */
        if (!lastSettledCart) throw new Error("final cart unavailable");
        if (cart && typeof cart.applyCartTruth === "function") {
          cart.applyCartTruth(lastSettledCart);
        } else if (cart && typeof cart.setCheckoutGated === "function") {
          cart.setCheckoutGated(lastSettledCart.item_count === 0);
        }
        return null;
      });
    };
    var run =
      cart && typeof cart.transact === "function"
        ? cart.transact(work)
        : work();
    run.then(null, function (err) {
      if (cart && typeof cart.setCheckoutGated === "function") {
        cart.setCheckoutGated(true);
      }
      if (cart && typeof cart.showCartError === "function") {
        cart.showCartError(
          (err && err.message) || "cart reconciliation failed",
        );
      }
    });
    return run;
  }

  /* ---------------------------------------------------- event triggers ---- */

  /* One trigger per cause. Quantity edits/removals and the add pipeline call
     reconcile() directly through their checkout gates (cart-drawer.js), so
     there is deliberately NO calmeden:cart-update fan-out. The initial
     fresh-load gate runs once from boot(); pageshow only fires the gate on a
     PERSISTED (bfcache) restore - a plain pageshow would duplicate the
     initial work. */
  document.addEventListener("submit", onSubmit);

  window.addEventListener("pageshow", function (event) {
    if (event.persisted) reconcileAndRefresh();
  });

  /* ----------------------------------------------------------------- boot -- */

  function boot() {
    loadGlobalConfig();
    initAll();
    /* Fresh-load reconcile gate: prove the cart clean once (checkout is
       gated for the duration via the narrow cart API). */
    reconcileAndRefresh();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* Theme editor / section re-render: re-init the (new) root only. */
  document.addEventListener("shopify:section:load", function (event) {
    var target = event.target || document;
    if (target.querySelector && target.querySelector("[data-cb-root]"))
      initAll();
  });

  window.CalmEdenBundle = {
    reconcile: reconcile,
    ownsForm: function (form) {
      return !!(
        configReady &&
        form &&
        typeof form.querySelector === "function" &&
        form.querySelector("[data-cb-root]")
      );
    },
    getLastSettledCart: function () {
      return lastSettledCart;
    },
    buildAddItems: buildAddItems,
    planAddItems: planAddItems,
    planReconcileOps: planReconcileOps,
    tierPay: tierPay,
    wantedGifts: wantedGifts,
    isOurGiftLine: isOurGiftLine,
    validateGiftConfig: validateGiftConfig,
    _state: function () {
      return {
        gifts: GIFTS,
        globalLadder: GLOBAL_LADDER,
        productLadders: PRODUCT_LADDERS,
        eligible: ELIGIBLE,
        nonQualifying: NON_QUALIFYING,
        freeVariantIds: FREE_VARIANT_IDS,
        configReady: configReady,
      };
    },
  };
})();
